import React, { useEffect, useRef, useState } from 'react';
import { Card, Container, Form, InputGroup, ListGroup, Row, Col, Badge } from 'react-bootstrap';
import { ACROSS, DOWN, otherDirection } from '../../libs/directionsLib';
import { PuzzleInteractionStatus, useClues, useCursor, usePuzzleInteractionStatus, useSavePuzzle, useSlotStructure } from './BoardInteractionContext';
import { getTop, isEntirelyInside } from '../../libs/miscLib';
import './CluesPanel.css';
import { useToastNotifications } from '../../libs/toastLib';
import ClueSuggestionCard from './ClueSuggestionCard';
import { suggestClue } from '../../libs/aiLib';
import { extractClueCommands, removeClueCommand, removeClueCommands } from '../../libs/puzzleLib';
import { FaLongArrowAltRight } from 'react-icons/fa';


export default function CluesPanel({pxHeight=400, triggerClueLinkageModal, ...props}) {

  const [showClueSuggestions, setShowClueSuggestions] = useState(false);   // show or hide clue suggestions card?
  const clueSuggestorMultiplier = showClueSuggestions ? 0.7 : 1;   // use this to make room for the ClueSuggestionCard if showing

  // Calculate heights of the clues panels
  pxHeight = Math.max((pxHeight || 250) - 60, 250) * clueSuggestorMultiplier;   // subtract some for the header heights
  const dominantBodyPxHeight = Math.round(pxHeight * 0.66);
  const submissiveBodyPxHeight = Math.round(pxHeight * 0.33);


  // Hook into board interaction context
  const { clues, setCluesAt } = useClues();

  const { cursorLoc, cursorDirection, handleClickOnLoc } = useCursor();
  const slotStructure = useSlotStructure();

  const { puzzleInteractionStatus } = usePuzzleInteractionStatus();

  const { requestPuzzleSave } = useSavePuzzle();

  const { postErrorNotification } = useToastNotifications();


  const primaryHighlightedSlotName = cursorLoc ? slotStructure.getSlotNameContainingCoordinates(cursorLoc, cursorDirection) : null;
  const secondaryHighlightedSlotName = cursorLoc ? slotStructure.getSlotNameContainingCoordinates(cursorLoc, otherDirection(cursorDirection)) : null;

  const currentDisplayWord = primaryHighlightedSlotName ? slotStructure.getWordInSlotName(primaryHighlightedSlotName, '·') : null;
  const currentWordIsComplete = primaryHighlightedSlotName ? slotStructure.slotStructureMap.get(primaryHighlightedSlotName).isFull : null;


  // These are always set in tandem; in fact, we can determine if placeholder is up-to-date by checking if currentSuggestionObj.answer === currentDisplayWord
  const [currentSuggestionObj, setCurrentSuggestionObj] = useState(null);
  const [currentClueSuggestionPlaceholder, setCurrentClueSuggestionPlaceholder] = useState(null);
  
  const [lastFocusedSlot, setLastFocusedSlot] = useState(null);  // the last slot whose text input got focused; used to determine if the text input has gained focus or if the grid still has focus

  // Store refs so we can scroll into view when necessary
  const acrossCluesRef = useRef(null);
  const downCluesRef = useRef(null);
  const clueComponentRefs = Array.from(clues.keys()).reduce((acc, value) => {
    acc[value] = React.createRef();
    return acc;
  }, {});

  // When cursor location changes, make sure the apparent clues are visible by scrolling
  useEffect(() => {
    if (primaryHighlightedSlotName && clueComponentRefs[primaryHighlightedSlotName] &&
        clueComponentRefs[primaryHighlightedSlotName].current && acrossCluesRef.current && downCluesRef.current) {
      const target = clueComponentRefs[primaryHighlightedSlotName].current;
      const primaryClueBox = cursorDirection === ACROSS ? acrossCluesRef.current : downCluesRef.current;

      if (!isEntirelyInside(target, primaryClueBox)) {
        primaryClueBox.scrollTo({top: getTop(target) - getTop(primaryClueBox) + primaryClueBox.scrollTop, behavior: 'smooth'});
      }
    }
  }, [clueComponentRefs, primaryHighlightedSlotName, cursorDirection]);
  useEffect(() => {
    if (secondaryHighlightedSlotName && clueComponentRefs[secondaryHighlightedSlotName] && clueComponentRefs[secondaryHighlightedSlotName].current) {
      const target = clueComponentRefs[secondaryHighlightedSlotName].current;
      const secondaryClueBox = cursorDirection === ACROSS ? downCluesRef.current : acrossCluesRef.current;

      if (!isEntirelyInside(target, secondaryClueBox)) {
        secondaryClueBox.scrollTo({top: getTop(target) - getTop(secondaryClueBox) + secondaryClueBox.scrollTop, behavior: 'smooth'});
      }
    }
  }, [clueComponentRefs, secondaryHighlightedSlotName, cursorDirection]);



  let acrossClueComponents = [];
  let downClueComponents = [];
  for (const [slotName, clueText] of clues.entries()) {
    const [slotNumber, slotDirection] = slotName.split('-');
    const displayWord = slotStructure.getWordInSlotName(slotName, '·');
    const extraClassName = (slotName === primaryHighlightedSlotName) ? ' primaryHighlighted' : (
      slotName === secondaryHighlightedSlotName ? ' secondaryHighlighted' : ''
    );

    function handleFocus(e) {
      if (slotName === secondaryHighlightedSlotName) {
        // Switch cursor direction
        handleClickOnLoc(cursorLoc);
      } else if (slotName !== primaryHighlightedSlotName) {
        // "Click" on first square of the focused slot
        const newLoc = slotStructure.getLocFromSlotName(slotName);
        handleClickOnLoc(newLoc, slotDirection);
      }

      // If it's a complete word, even if already filled clue, get the suggested clue as a green placeholder
      if (displayWord && slotStructure.slotStructureMap.get(slotName).isFull) {
        // aiLib takes care of ensuring non-stale (i.e. we haven't asked for a new word's clue suggestion yet if this is returning)
        suggestClue(displayWord).then(clueSuggestionObj => {
          if (!clueSuggestionObj) return;
          // We just need to update the placeholder here
          setCurrentClueSuggestionPlaceholder(clueSuggestionObj?.clueOptions?.[0]);
          // We also need to update the suggestion object for the ClueSuggestorCard
          setCurrentSuggestionObj(clueSuggestionObj);
        });
      }

      setLastFocusedSlot(slotName);
    }

    // const placeholderToShow = currentSuggestionObj?.answer === currentDisplayWord ? currentClueSuggestionPlaceholder : slotName;
    // const greenPlaceholderStyling = showClueSuggestions && slotName === primaryHighlightedSlotName && currentSuggestionObj?.answer === currentDisplayWord ? ' greenPlaceholder' : '';

    function handleBlur() {
      let newClueText = clueComponentRefs[slotName].current.value;
      console.log(newClueText)
      console.log(clues.get(slotName))

      // Don't set the clues if there isn't a change - this leads to unnecessary calls in live mode
      if (newClueText !== removeClueCommands(clues.get(slotName))) {
        // Ensure clue text is less than 800 characters (arbitrary, but hopefully avoids us ever running into the 4KB limit for dynamoDb puts)
        if (newClueText.length > 800) {
          newClueText = newClueText.slice(0, 800);
          postErrorNotification('Clue too long!', 'We cut your clue down to the first 800 characters to keep our database writes manageable.', ['puzzle-specific']);
        }

        // Add back any clue commands that were removed for the uncontrolled input's default value
        const clueCommands = extractClueCommands(clues.get(slotName));
        for (const cc of clueCommands) {
          newClueText = newClueText + '$' + cc + '$';
        }
        
        // Update clues
        setCluesAt([{ slotName: slotName, clueText: newClueText }]);
      }
    }

    const linkedSlotsCondensed = extractClueCommands(clues.get(slotName), /^link:\d+[AD]$/).map(cc => cc.slice(5)).sort((a, b) => {
      if (a.endsWith('A') && b.endsWith('D')) return -1;
      if (a.endsWith('B') && a.endsWith('A')) return 1;
      return parseInt(a) - parseInt(b);
    });

    const clueComponent = (
      <ListGroup.Item className='p-0' key={slotName + '-cluecontrol'}>
        <InputGroup key={clueText /*adding this key forces the Form.Control to rerender when clueText (i.e. defaultValue) is changed*/}>
          <InputGroup.Text className={'clueNumberLabel' + (extraClassName === '' ? '' : (extraClassName + 'Dark'))}>
            {slotNumber}
          </InputGroup.Text>
          <Form.Control 
            ref={clueComponentRefs[slotName]}
            className={(clueText.includes('$hide$') ? 'text-secondary ' : '') + 'smallClueInput' + extraClassName}
            type='text'
            name={'clue-' + slotName}
            placeholder={slotName}
            disabled={puzzleInteractionStatus !== PuzzleInteractionStatus.EDITABLE && puzzleInteractionStatus !== PuzzleInteractionStatus.EDITABLE_GUEST}
            defaultValue={removeClueCommands(clueText)}  // uncontrolled form allows quicker typing without lag
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={e => {
              // Want to prevent default responses inside input component for ctrl/cmd+s
              if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                setCluesAt([{ slotName: slotName, clueText: clueComponentRefs[slotName].current.value }]);
                requestPuzzleSave();
              } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                if (clueComponentRefs[slotName].current && !clueComponentRefs[slotName].current.value && displayWord === currentSuggestionObj.answer && currentClueSuggestionPlaceholder) {
                  clueComponentRefs[slotName].current.value = currentClueSuggestionPlaceholder;
                }
                const nextSlotName = slotStructure.getNextSlotName(slotName);
                if (nextSlotName) {
                  clueComponentRefs[nextSlotName].current.focus();
                } else {
                  clueComponentRefs[slotName].current.blur();
                }
              } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                if (e.shiftKey) {
                  const previousSlotName = slotStructure.getPreviousSlotName(slotName);
                  if (previousSlotName) {
                    clueComponentRefs[previousSlotName].current.focus();
                  } else {
                    clueComponentRefs[slotName].current.blur();
                  }
                } else {
                  const nextSlotName = slotStructure.getNextSlotName(slotName);
                  if (nextSlotName) {
                    clueComponentRefs[nextSlotName].current.focus();
                  } else {
                    clueComponentRefs[slotName].current.blur();
                  }
                }
              }
            }}
          />
          <div className={'d-flex align-items-center gap-1 ' + extraClassName}>

            {clueText.includes('$hide$') && (
              <Badge
                className='zoomable clickable'
                bg='secondary'
                onClick={() => {
                  handleFocus(slotName);
                  // Unhide the clue
                  setCluesAt([{ slotName, clueText: removeClueCommand(clueText, 'hide') }]);
                }}
              >clue hidden</Badge>
            )}

            {linkedSlotsCondensed.length > 0 && (
              <Badge className='zoomable clickable' bg='info' onClick={() => {
                handleFocus(slotName);
                triggerClueLinkageModal();
              }}>
                link <FaLongArrowAltRight /> {linkedSlotsCondensed.join(' ')}
              </Badge>
            )}

            <span className={`px-1 block-text small ${slotStructure.slotStructureMap.get(slotName).isFull ? 'text-muted' : 'text-danger'}`}>{displayWord}</span>
          </div>
        </InputGroup>
      </ListGroup.Item>
    );
    if (slotDirection === DOWN) {
      downClueComponents.push(clueComponent);
    } else {
      acrossClueComponents.push(clueComponent);
    }
  }




  return (
    <Container className='p-0' {...props}>
      <Row>
        <Col>
          <Card className='cluesCard'>
            <Card.Header>
              <span className={'me-2 block-text' + (pxHeight < 310 ? ' small' : '')} style={{ transition: 'font-size 300ms ease '}}>ACROSS clues</span>
            </Card.Header>

            <Card.Body ref={acrossCluesRef} style={{height: `${cursorDirection === ACROSS ? dominantBodyPxHeight : submissiveBodyPxHeight}px`, transition: 'height 300ms ease' }}>
              <ListGroup variant='flush'>
                {acrossClueComponents}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className='cluesCard'>
            <Card.Header>
              <span className={'me-2 block-text' + (pxHeight < 310 ? ' small' : '')} style={{ transition: 'font-size 300ms ease '}}>DOWN clues</span>
            </Card.Header>

            <Card.Body ref={downCluesRef} style={{height: `${cursorDirection === DOWN ? dominantBodyPxHeight : submissiveBodyPxHeight}px`, transition: 'height 300ms ease' }}>
              <ListGroup variant='flush'>
                {downClueComponents}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {currentDisplayWord && <ClueSuggestionCard
        displayWord={currentDisplayWord}
        isComplete={currentWordIsComplete}
        isFocused={lastFocusedSlot === primaryHighlightedSlotName}
        isLoading={currentDisplayWord !== currentSuggestionObj?.answer}
        clueSuggestionObj={currentSuggestionObj}
        setPlaceholderText={setCurrentClueSuggestionPlaceholder}
        setClueText={text => {
          if (clueComponentRefs[primaryHighlightedSlotName].current) {
            clueComponentRefs[primaryHighlightedSlotName].current.value = text;
            clueComponentRefs[primaryHighlightedSlotName].current.focus();
          }
        }}
        showBody={showClueSuggestions}
        setShowBody={setShowClueSuggestions}
      />}
    </Container>
  );
}